// Updated src/components/books/renderers/InteractiveElementRenderer.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Star, 
  Upload, 
  Calendar, 
  Check,
  X,
  AlertCircle,
  Download,
  Music,
  Video,
  FileText
} from 'lucide-react';
import { MathField } from '../../math/MathField';
import { MathText } from '../../math/MathText';
import { firestoreService } from '../../../services/firestoreService';

export const InteractiveElementRenderer = ({ 
  message, 
  value, 
  onChange, 
  disabled = false,
  className = '',
  responseId,
  bookId,
  onUserResponse,
  userResponses = {}
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [fileName, setFileName] = useState('');

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || '');
    
    // Handle multiselect values
    if (message.userResponseType?.startsWith('multiselect') || message.userResponseType?.startsWith('choice')) {
      const { type } = parseResponseType(message.userResponseType);
      if (type === 'multiselect' || type === 'choice') {
        setSelectedOptions(value ? value.split(',').map(v => v.trim()) : []);
      }
    }
    
    // Handle rating values
    if (message.userResponseType?.startsWith('rating')) {
      setRating(parseInt(value) || 0);
    }
  }, [value, message.userResponseType]);

  const handleChange = (newValue, additionalData = {}) => {
    setLocalValue(newValue);
    
    // Call onChange with value and metadata
    onChange?.(newValue, {
      messageId: message.messageId,
      variable: message.variable,
      ...additionalData
    });
  };

  useEffect(() => {
    if (value && typeof value === 'string' && value.startsWith('http')) {
      // Check if we have metadata stored
      const fieldName = message.variable;
      const metadataKey = `${fieldName}_metadata`;
      // You'd get this from the userResponses context
      const metadata = userResponses[metadataKey];
      
      setUploadedFile({
        downloadURL: value,
        ...metadata
      });
    }
  }, [value]);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file || !responseId) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      // Upload file using FirestoreService
      const fileInfo = await firestoreService.uploadResponseFile(
        responseId, 
        message.variable, 
        file, 
        bookId
      );

      // Update response with file info
      await firestoreService.updateBookResponseWithFile(
        responseId, 
        message.variable, 
        fileInfo
      );

      setUploadedFile(fileInfo);
      onChange(fileInfo.downloadURL, message.variable);

      console.log('File uploaded successfully:', fileInfo);
      
    } catch (error) {
      console.error('File upload failed:', error);
      alert('File upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const removeFile = async () => {
    if (!uploadedFile || !responseId) return;

    try {
      // Delete file from storage
      await firestoreService.deleteResponseFile(uploadedFile.storagePath);
      
      // Update response to remove file reference
      await firestoreService.updateBookResponse(responseId, {
        [`responses.${message.variable}`]: null,
        [`responses.${message.variable}_metadata`]: null
      });

      setUploadedFile(null);
      onChange('', message.variable);
      
    } catch (error) {
      console.error('Error removing file:', error);
    }
  };

  const parseResponseType = (responseType) => {
    
    if (!responseType) return { type: 'text', options: [], config: {} };
    
    // Extract base type
    const [baseType] = responseType.split('(');
    
    // Find the config content by finding matching parentheses
    const configContent = extractConfigFromParentheses(responseType);
    
    let config = {};
    let options = [];
    
    if (configContent) {
        
        // Enhanced parsing that handles quoted options
        const parsedConfig = parseConfigStringWithQuotes(configContent);
        config = parsedConfig.config;
        options = parsedConfig.options;
        
    } else {
        console.log('No config content found');
    }
    
    const result = {
        type: baseType.trim(),
        options,
        config
    };
    
    return result;
    };

    const extractConfigFromParentheses = (responseType) => {
    // Find the first opening parenthesis
    const startIndex = responseType.indexOf('(');
    if (startIndex === -1) return null;
    
    let parenCount = 0;
    let i = startIndex;
    
    // Find the matching closing parenthesis
    while (i < responseType.length) {
        if (responseType[i] === '(') {
        parenCount++;
        } else if (responseType[i] === ')') {
        parenCount--;
        if (parenCount === 0) {
            // Found the matching closing parenthesis
            return responseType.substring(startIndex + 1, i);
        }
        }
        i++;
    }
    
    return null; // No matching closing parenthesis found
    };

    const parseConfigStringWithQuotes = (configStr) => {
    const config = {};
    const options = [];
    
    let i = 0;
    const chars = configStr.split('');
    
    while (i < chars.length) {
        // Skip whitespace
        while (i < chars.length && chars[i] === ' ') i++;
        if (i >= chars.length) break;
        
        // Find the key
        let key = '';
        while (i < chars.length && chars[i] !== ':') {
        key += chars[i];
        i++;
        }
        key = key.trim();
        
        if (i >= chars.length || chars[i] !== ':') break;
        i++; // Skip the colon
        
        // Skip whitespace after colon
        while (i < chars.length && chars[i] === ' ') i++;
        
        if (key === 'options') {
        // Special handling for options with quote support
        const optionsResult = parseQuotedOptions(chars, i);
        options.push(...optionsResult.options);
        i = optionsResult.nextIndex;
        } else {
        // Parse regular config value
        const valueResult = parseConfigValue(chars, i);
        const value = valueResult.value;
        console.log('Config key-value:', key, '=', value);
        config[key] = value === 'true' ? true : value === 'false' ? false : value;
        i = valueResult.nextIndex;
        }
        
        // Skip comma and whitespace
        while (i < chars.length && (chars[i] === ',' || chars[i] === ' ')) i++;
    }
    
    return { config, options };
    };

    const parseQuotedOptions = (chars, startIndex) => {
    const options = [];
    let i = startIndex;
    
    while (i < chars.length) {
        // Skip whitespace
        while (i < chars.length && chars[i] === ' ') i++;
        if (i >= chars.length) break;
        
        // Check if this is the start of a new config key
        const remainingStr = chars.slice(i).join('');
        const nextKeyMatch = remainingStr.match(/^[a-zA-Z]+:/);
        if (nextKeyMatch) {
        break;
        }
        
        if (chars[i] === '"') {
        i++; // Skip opening quote
        let option = '';
        let escaped = false;
        
        while (i < chars.length) {
            const char = chars[i];
            
            if (escaped) {
            option += char;
            escaped = false;
            } else if (char === '\\') {
            option += char;
            escaped = true;
            } else if (char === '"') {
            i++; // Skip closing quote
            break;
            } else {
            option += char;
            }
            i++;
        }
        
        if (option.trim()) {
            console.log('Adding quoted option:', option.trim());
            options.push(option.trim());
        }
        
        // Skip comma and whitespace after quoted option
        while (i < chars.length && (chars[i] === ',' || chars[i] === ' ')) i++;
        
        } else {
        // Parse unquoted option (fallback to old method)
        const unquotedResult = parseUnquotedOption(chars, i);
        if (unquotedResult.option.trim()) {
            options.push(unquotedResult.option.trim());
        }
        i = unquotedResult.nextIndex;
        
        // Skip comma and whitespace
        while (i < chars.length && (chars[i] === ',' || chars[i] === ' ')) i++;
        }
    }
    
    return { options, nextIndex: i };
    };

    const parseUnquotedOption = (chars, startIndex) => {
    let option = '';
    let i = startIndex;
    let inLatex = false;
    let latexDepth = 0;
    
    while (i < chars.length) {
        const char = chars[i];
        const nextChar = i + 1 < chars.length ? chars[i + 1] : '';
        
        // Check for LaTeX delimiters
        if (char === '\\' && (nextChar === '(' || nextChar === '[')) {
        inLatex = true;
        latexDepth++;
        option += char + nextChar;
        i += 2;
        continue;
        } else if (char === '\\' && (nextChar === ')' || nextChar === ']')) {
        latexDepth--;
        if (latexDepth <= 0) {
            inLatex = false;
            latexDepth = 0;
        }
        option += char + nextChar;
        i += 2;
        continue;
        }
        
        // Handle commas - only split if we're not inside LaTeX
        if (char === ',' && !inLatex) {
        // Check if this comma is ending the options (next key starts)
        const remainingStr = chars.slice(i + 1).join('').trim();
        if (remainingStr.match(/^[a-zA-Z]+:/)) {
            // This comma ends the options
            return { option, nextIndex: i };
        } else {
            // This comma separates options
            return { option, nextIndex: i + 1 };
        }
        }
        
        // Check if we've reached the next config key (not inside LaTeX)
        if (!inLatex && char === ',' && i + 1 < chars.length) {
        const remainingStr = chars.slice(i + 1).join('').trim();
        if (remainingStr.match(/^[a-zA-Z]+:/)) {
            // We've reached the next config key
            return { option, nextIndex: i };
        }
        }
        
        option += char;
        i++;
    }
    
    return { option, nextIndex: i };
    };

    const parseConfigValue = (chars, startIndex) => {
    let value = '';
    let i = startIndex;
    
    while (i < chars.length && chars[i] !== ',') {
        // Check if we've reached the next key
        const remainingStr = chars.slice(i).join('');
        if (remainingStr.match(/^[a-zA-Z]+:/)) {
        break;
        }
        value += chars[i];
        i++;
    }
    
    return { value: value.trim(), nextIndex: i };
    };

  const { type, options, config } = parseResponseType(message.userResponseType);

  const interpolateVariables = (text, responses) => {
    if (!text || typeof text !== 'string') return text;
    
    return text.replace(/\{([^}]+)\}/g, (match, varName) => {
      const trimmedVarName = varName.trim();
      
      if (responses && responses.hasOwnProperty(trimmedVarName)) {
        const value = responses[trimmedVarName];
        return value !== null && value !== undefined ? String(value) : match;
      }
      
      const headerMappedKey = Object.keys(responses || {}).find(key => 
        key.startsWith(`${trimmedVarName}?`)
      );
      
      if (headerMappedKey) {
        const value = responses[headerMappedKey];
        return value !== null && value !== undefined ? String(value) : match;
      }
      
      return match;
    });
  };

  const processedPlaceholder = interpolateVariables(config.placeholder || '', userResponses);

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return Image;
    if (contentType?.startsWith('audio/')) return Music;  
    if (contentType?.startsWith('video/')) return Video;
    if (contentType?.includes('pdf')) return FileText;
    return File;
  };

  // Math Input
  if (type === 'math') {
    return (
      <div className={`math-input-container ${className}`}>
        <MathField
          value={localValue}
          onChange={handleChange}
          placeholder={config.placeholder || 'Enter mathematical expression...'}
          required={config.required}
          virtualKeyboards={config.keyboards || ['geometric', 'algebra', 'trigonometry', '[default]']}
          className="w-full"
        />
        
        {/* Helper text */}
        <div className="mt-2 text-xs text-gray-500">
          Use the keyboard toolbar or type LaTeX directly
        </div>
      </div>
    );
  }

  // Text Input
  if (type === 'text' || type === 'email' || type === 'number') {
    return (
      <input
        type={type}
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled}
        required={config.required}
        placeholder={config.placeholder}
        min={config.min}
        max={config.max}
        step={config.step}
        className={`w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
      />
    );
  }

  // Textarea
  if (type === 'textarea' || type === 'longtext') {
    return (
      <textarea
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled}
        required={config.required}
        placeholder={config.placeholder}
        rows={config.rows || 4}
        maxLength={config.maxLength}
        className={`w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${className}`}
      />
    );
  }

  // Choice/Select (single or multi) - WITH MATH SUPPORT
  if (type === 'choice' || type === 'select' || type === 'multiselect') {
    const isMultiSelect = config.style === 'multiselect' || type === 'multiselect';
    const isRequired = config.required === true;

    // Enhanced button styling in the buttons section:
    if (type === 'buttons' || (type === 'choice' && config.style === 'buttons')) {
    return (
        <div className={`space-y-3 ${className}`}>
        {options.map((option, index) => (
            <button
            key={index}
            type="button"
            onClick={() => handleChange(option)}
            disabled={disabled}
            className={`
                w-full p-4 text-left border-2 rounded-lg transition-all duration-200 
                ${localValue === option
                ? 'border-blue-500 bg-white text-blue-900 shadow-sm' 
                : 'border-gray-100 hover:border-blue-300 hover:bg-blue-50 shadow-md'
                } 
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:scale-[0.98]'}
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
            `}
            >
            <div className="flex items-center">
                {localValue === option && (
                <div className="w-4 h-4 bg-blue-500 rounded-full mr-3 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                )}
                {localValue !== option && (
                <div className="w-4 h-4 border-2 border-gray-300 rounded-full mr-3"></div>
                )}
                <MathText className="text-sm leading-relaxed flex-1">
                {option}
                </MathText>
            </div>
            </button>
        ))}
        {config.required && !localValue && (
            <p className="mt-2 text-sm text-red-600">Please select an option</p>
        )}
        </div>
    );
    }
    
    if (isMultiSelect) {
        return (
            <div className={className}>
            <div className="space-y-3">
                {options.map((option, index) => {
                const isSelected = selectedOptions.includes(option);
                return (
                    <label 
                    key={`${index}-${isSelected ? 'selected' : 'unselected'}`} 
                    className="flex items-start space-x-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                        const newSelected = e.target.checked
                            ? [...selectedOptions, option]
                            : selectedOptions.filter(opt => opt !== option);
                        setSelectedOptions(newSelected);
                        handleChange(newSelected.join(', '));
                        }}
                        disabled={disabled}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                        <MathText 
                        key={`math-${index}-${isSelected ? 'sel' : 'unsel'}-${Date.now()}`}
                        className="text-sm text-gray-700 leading-relaxed"
                        >
                        {option}
                        </MathText>
                    </div>
                    </label>
                );
                })}
            </div>
            {isRequired && selectedOptions.length === 0 && (
                <p className="mt-2 text-sm text-red-600">This field is required</p>
            )}
            </div>
        );
    }
    // Single select dropdown - WITH MATH SUPPORT
    return (
      <select
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled}
        required={isRequired}
        className={`w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
      >
        <option value="">{config.placeholder || 'Select an option...'}</option>
        {options.map((option, index) => (
          <option key={index} value={option}>
            {/* Note: Math can't render in option tags, so we show plain text */}
            {option.replace(/\\\(/g, '').replace(/\\\)/g, '').replace(/\\\[/g, '').replace(/\\\]/g, '')}
          </option>
        ))}
      </select>
    );
  }

  // Rating
  if (type === 'rating') {
    const maxRating = parseInt(config.max) || 5;
    
    return (
      <div className={className}>
        <div className="flex items-center space-x-1">
          {[...Array(maxRating)].map((_, index) => {
            const starValue = index + 1;
            return (
              <button
                key={index}
                type="button"
                onClick={() => {
                  setRating(starValue);
                  handleChange(starValue.toString());
                }}
                onMouseEnter={() => setHoverRating(starValue)}
                onMouseLeave={() => setHoverRating(0)}
                disabled={disabled}
                className={`p-1 transition-colors ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <Star
                  className={`h-6 w-6 ${
                    starValue <= (hoverRating || rating)
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            );
          })}
          <span className="ml-2 text-sm text-gray-600">
            {rating > 0 ? `${rating}/${maxRating}` : 'Rate this'}
          </span>
        </div>
      </div>
    );
  }

  // Date Input
  if (type === 'date') {
    return (
      <div className={`relative ${className}`}>
        <input
          type="date"
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <Calendar className="absolute right-3 top-3 h-5 w-5 text-gray-400 pointer-events-none" />
      </div>
    );
  }

  // File Upload
  if (type === 'file') {
    return (
      <div className={`file-upload-container ${className}`}>
        {uploadError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">{uploadError}</span>
          </div>
        )}
        
        {!uploadedFile && !uploading ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              disabled={disabled || uploading}
              className="hidden"
              accept={config.allowedTypes?.join(',')}
            />
            
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            
            <p className="text-sm text-gray-600 mb-2">
              {config.placeholder || 'Click to upload a file'}
            </p>
            
            {(config.allowedTypes || config.maxSize) && (
              <p className="text-xs text-gray-500 mb-3">
                {config.allowedTypes && `Allowed: ${config.allowedTypes.join(', ')}`}
                {config.allowedTypes && config.maxSize && ' • '}
                {config.maxSize && `Max size: ${config.maxSize}MB`}
              </p>
            )}
            
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Choose File
            </button>
          </div>
        ) : uploading ? (
          <div className="border border-gray-200 rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">
              Uploading... {uploadProgress > 0 ? `${Math.round(uploadProgress)}%` : ''}
            </p>
          </div>
        ) : uploadedFile && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center min-w-0 flex-1">
                {React.createElement(getFileIcon(uploadedFile.contentType), {
                  className: "h-5 w-5 text-gray-500 flex-shrink-0 mr-3"
                })}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {uploadedFile.originalName || uploadedFile.fileName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {uploadedFile.size ? `${Math.round(uploadedFile.size / 1024)} KB` : ''}
                    {uploadedFile.contentType && ` • ${uploadedFile.contentType}`}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 ml-3">
                <a
                  href={uploadedFile.downloadURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                  title="View/Download file"
                >
                  <Download className="h-4 w-4" />
                </a>
                
                {!disabled && (
                  <button
                    onClick={removeFile}
                    className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                    title="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Preview for images */}
            {uploadedFile.contentType?.startsWith('image/') && (
              <div className="mt-3">
                <img 
                  src={uploadedFile.downloadURL} 
                  alt="Upload preview"
                  className="max-w-full h-32 object-cover rounded border"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Default fallback to textarea
  return (
    <textarea
      value={localValue}
      onChange={(e) => handleChange(e.target.value)}
      disabled={disabled}
      placeholder="Enter your response..."
      rows={3}
      className={`w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${className}`}
    />
  );
};

export default InteractiveElementRenderer;